import { useLocation } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";

export function TermsPage() {
  usePageMeta({
    title: "Terms of Service — QuotedsArrow",
    description: "Read the QuotedsArrow terms of service.",
  });

  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-stone-50 font-sans">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-stone-50/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-900 text-white text-sm font-bold">
              →
            </div>
            <span className="font-serif text-lg text-stone-900">QuotedsArrow</span>
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-stone-500 hover:text-stone-900 transition"
          >
            ← Back
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="mb-10">
          <h1 className="font-serif text-4xl text-stone-900 mb-3">Terms of Service</h1>
          <p className="text-sm text-stone-500">Last updated: May 21, 2026</p>
        </div>

        <div className="space-y-10 text-stone-700 leading-relaxed">
          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Agreement</h2>
            <p>
              By creating an account or using QuotedsArrow ("the Service"), you agree to these
              Terms of Service. If you do not agree, do not use the Service. These terms form a
              binding agreement between you and QuotedsArrow.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Eligibility</h2>
            <p>
              You must be at least 13 years old to use QuotedsArrow. By using the Service, you
              represent that you meet this requirement. If you are using the Service on behalf of
              an organisation, you represent that you are authorised to bind that organisation to
              these terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Your Account</h2>
            <p className="mb-3">
              You are responsible for maintaining the security of your account and for all
              activity that occurs under it. Do not share your login credentials with others. If
              you suspect unauthorised access to your account, notify us immediately at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              .
            </p>
            <p>
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Acceptable Use</h2>
            <p className="mb-3">When using the Service, you agree not to:</p>
            <ul className="space-y-2 list-none pl-0">
              {[
                "Submit quote content that is hateful, discriminatory, threatening, harassing, or sexually explicit.",
                "Submit content that infringes the intellectual property rights of others. Quotes from living, commercially active authors should only be submitted if they are clearly in the public domain or you have explicit permission.",
                "Attempt to gain unauthorised access to any part of the Service or its infrastructure.",
                "Use the Service to send spam, conduct phishing attacks, or distribute malware.",
                "Scrape, crawl, or programmatically extract quote data from the Service without prior written permission.",
                "Use automated tools to create accounts or submit quotes at scale.",
              ].map((item, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">User-Submitted Content</h2>
            <div className="space-y-3">
              <p>
                You retain ownership of any original content you submit, including quotes you
                authored yourself. By submitting content to QuotedsArrow, you grant us a
                worldwide, non-exclusive, royalty-free licence to store, display, and distribute
                that content as part of the Service and our community library.
              </p>
              <p>
                All submissions are reviewed by our moderation team before being published.
                We reserve the right to reject, edit, or remove any submission at our discretion,
                including submissions that violate these terms or our community standards.
              </p>
              <p>
                Quotes from historical figures, authors, and public domain sources are shared for
                educational and cultural purposes. If you believe any content infringes your
                intellectual property rights, contact us at{" "}
                <a
                  href="mailto:privacy@quotedsarrow.com"
                  className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
                >
                  privacy@quotedsarrow.com
                </a>{" "}
                and we will promptly investigate.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Our Intellectual Property</h2>
            <p>
              The QuotedsArrow name, logo, design, software, and curation methodology are owned
              by us. You may not copy, reproduce, or distribute any part of the Service without
              our express written permission, except as permitted by applicable law.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">The Service "As Is"</h2>
            <p>
              QuotedsArrow is provided on an "as is" and "as available" basis, without warranties
              of any kind, either express or implied. We do not guarantee that the Service will be
              uninterrupted, error-free, or free of harmful components. We make no warranties about
              the accuracy, completeness, or reliability of any quote content in the library.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, QuotedsArrow and its operators shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages —
              including loss of data, revenue, or goodwill — arising from your use of or inability
              to use the Service, even if we have been advised of the possibility of such damages.
              Our total liability to you for any claim arising out of or relating to these terms or
              the Service shall not exceed the greater of $10 or the amount you paid us in the
              12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Termination</h2>
            <p>
              You may stop using the Service at any time. You may request deletion of your account
              by emailing us at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              . We reserve the right to suspend or terminate your access if you violate these terms,
              with or without notice.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Governing Law</h2>
            <p>
              These terms are governed by the laws of the United States, without regard to
              conflict-of-law principles. Any disputes will be resolved through binding arbitration
              or in a court of competent jurisdiction in the United States.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes
              by updating the date at the top of this page. Continued use of the Service after
              a change constitutes your acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Contact</h2>
            <p>
              Questions about these Terms? Reach us at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-stone-200 bg-stone-50 py-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between">
          <span className="text-xs text-stone-400">© 2026 QuotedsArrow</span>
          <div className="flex gap-4 text-xs text-stone-500">
            <button onClick={() => navigate("/privacy")} className="hover:text-stone-900 transition">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-stone-900 transition">Terms</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
